import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'

class StompService {
  constructor() {
    this.client = null
    this.subscriptions = {}
    this.retryCount = 0
    this.maxRetries = 200 // ⬅️ Change this to adjust retry limit
  }

  connect(initialSubscriptions = {}) {

    if (this.client) {
      if (this.client.connected || this.client.active) {
        return Promise.resolve(true)
      }
    }

    return new Promise((resolve, reject) => {

      this.client = new Client({
        webSocketFactory: () => new SockJS(`${process.env.VUE_APP_API_URL}websocket-endpoint`),
        reconnectDelay: 1000,
        maxReconnectDelay: 200000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000
      })

      this.client.onConnect = () => {
        // console.log('✅ STOMP connected')
        this.retryCount = 0 // ✅ Reset on successful connection
        Object.entries(this.subscriptions).forEach(([destination, { callback }]) => {
          this._internalSubscribe(destination, callback)
        })
        resolve(true)
      }

      this.client.onWebSocketClose = () => {
        // console.log('🔻 STOMP onWebSocketClose')
        // Clean up existing subscriptions
        Object.keys(this.subscriptions).forEach((key) => {
          if (this.subscriptions[key]?.subscription) {
            this.subscriptions[key].subscription = null
          }
        })

        this.retryCount++
        console.warn(`🔁 STOMP retry attempt ${this.retryCount}/${this.maxRetries}`)

        if (this.retryCount >= this.maxRetries) {
          console.error('🚫 Max STOMP retry limit reached. Disconnecting.')
          this.client.deactivate()
          this.client = null
          resolve(false)
        } else if (!this.client?.connected) {
          console.error('WebSocket connection closed before successful STOMP connection')
          resolve(false)
        }
      }

      this.client.onStompError = (frame) => {
        // console.error('❌ STOMP error', frame.headers['message'], frame.body)
        console.error(`STOMP error: ${frame.headers['message'] || 'Unknown error'}`)
        resolve(false)
      }

      this.client.activate()

      if (Object.entries(initialSubscriptions).length > 0) {
        Object.entries(initialSubscriptions).forEach(([subscriptionKey, { destination, callback }]) => {
          this.subscribe(destination, callback)
        })
      }

    })
  }

  subscribe(destination, callback) {
    // If we're already subscribed to this topic, skip
    if (this.subscriptions[destination]?.subscription) {
      console.warn(`⚠️ Already subscribed to ${destination}`)
      return
    }

    // Save the callback so we can re-subscribe after reconnect
    this.subscriptions[destination] = {
      callback,
      subscription: null,
    }

    if (this.client && this.client.connected) {
      this._internalSubscribe(destination, callback)
    } else {
      // Wait for connection before subscribing
      const wait = setInterval(() => {
        if (this.client && this.client.connected) {
          clearInterval(wait)

          // Double-check again before subscribing
          if (!this.subscriptions[destination]?.subscription) {
            this._internalSubscribe(destination, callback)
          }
        }
      }, 300)
    }
  }

  _internalSubscribe(destination, callback) {
    const record = this.subscriptions[destination]

    if (!record || record.subscription) {
      return // Already subscribed
    }

    record.subscription = this.client.subscribe(destination, (message) => {
      callback(JSON.parse(message.body))
    })
  }

  unsubscribe(destination) {
    if (this.subscriptions[destination]?.subscription) {
      this.subscriptions[destination].subscription.unsubscribe()
      delete this.subscriptions[destination]
    }
  }

  send(destination, payload) {
    if (this.client && this.client.connected) {
      this.client.publish({
        destination,
        body: JSON.stringify(payload),
      })
    }
  }

  disconnect() {
    if (this.client && this.client.active) {
      Object.keys(this.subscriptions).forEach((destination) => {
        this.unsubscribe(destination)
      })
      this.client.deactivate()
      this.client = null
      this.retryCount = 0 // ✅ Reset retries on manual disconnect
      // console.log('🛑 STOMP disconnected')
    }
  }
}

export default new StompService()
